import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon, IonInput, IonCheckbox,
    IonSelect, IonSelectOption, IonText, IonSpinner, IonLabel,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { SitesResponse, UserTypesResponse } from '@/types/response/lookupResponses';

interface AddUserModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    sites: SitesResponse[];
    userTypes: UserTypesResponse[];
    emailRegex: string;
    onSaved: () => void;
}

interface FormData {
    userName: string;
    title: string;
    password: string;
    rePassword: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    organization: string;
    isActive: boolean;
    isEnterpriseAdmin: boolean;
    assignedToContract: boolean;
    userTypeId: string;
    defaultSiteId: string;
}

const EMPTY_FORM: FormData = {
    userName: '', title: '', password: '', rePassword: '',
    firstName: '', lastName: '', emailAddress: '', organization: '',
    isActive: true, isEnterpriseAdmin: false, assignedToContract: true,
    userTypeId: '', defaultSiteId: '',
};

const AddUserModal: React.FC<AddUserModalProps> = ({
    isOpen, onDidDismiss, sites, userTypes, emailRegex, onSaved,
}) => {
    const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const forceClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            forceClosingRef.current = false;
            setFormData({ ...EMPTY_FORM });
            setErrors({});
        }
    }, [isOpen]);

    const update = (field: keyof FormData, value: unknown) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validate = (): boolean => {
        const e: Record<string, string> = {};
        if (!formData.userName.trim()) e.userName = 'User Name is required.';
        if (!formData.firstName.trim()) e.firstName = 'First Name is required.';
        if (!formData.lastName.trim()) e.lastName = 'Last Name is required.';
        if (!formData.emailAddress.trim()) {
            e.emailAddress = 'Email Address is required.';
        } else if (emailRegex && !new RegExp(emailRegex).test(formData.emailAddress)) {
            e.emailAddress = 'Email Address format is invalid.';
        }
        if (!formData.password) e.password = 'Temporary Password is required.';
        if (!formData.rePassword) {
            e.rePassword = 'Please re-enter the password.';
        } else if (formData.password && formData.password !== formData.rePassword) {
            e.rePassword = 'Passwords do not match.';
        }
        if (!formData.isEnterpriseAdmin && formData.assignedToContract && !formData.userTypeId) {
            e.userTypeId = 'User Type is required when assigned to module.';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await apiService.saveUserDetails({
                UserName: formData.userName.trim(),
                Password: formData.password,
                Title: formData.title.trim(),
                FirstName: formData.firstName.trim(),
                LastName: formData.lastName.trim(),
                EmailAddress: formData.emailAddress.trim(),
                Organization: formData.organization.trim(),
                IsActive: formData.isActive,
                IsEnterpriseAdmin: formData.isEnterpriseAdmin,
                AssignedToContract: formData.assignedToContract,
                UserTypeId: formData.userTypeId ? parseInt(formData.userTypeId, 10) : null,
                DefaultSiteId: formData.defaultSiteId ? parseInt(formData.defaultSiteId, 10) : null,
            });
            setToast({ message: 'User created successfully.', variant: 'success' });
            onSaved();
            forceClosingRef.current = true;
            onDidDismiss();
        } catch {
            setToast({ message: 'Failed to create user.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const showSiteSelect = formData.isEnterpriseAdmin || formData.assignedToContract;
    const showUserType = !formData.isEnterpriseAdmin && formData.assignedToContract;

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={() => { if (!forceClosingRef.current) onDidDismiss(); }}
                style={{ '--width': '540px', '--height': '680px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>Add New User</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={onDidDismiss}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1.25rem', '--padding-end': '1.25rem', '--padding-top': '1rem', '--padding-bottom': '0.5rem' }}>
                    {isSaving ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                            <IonSpinner />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {/* Row: UserName */}
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>User Name *</IonLabel>
                                <IonInput value={formData.userName} onIonInput={e => update('userName', e.detail.value ?? '')}
                                    style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                {errors.userName && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.userName}</IonText>}
                            </div>
                            {/* Row: Title */}
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Title</IonLabel>
                                <IonInput value={formData.title} onIonInput={e => update('title', e.detail.value ?? '')}
                                    style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                            </div>
                            {/* Row: First/Last Name */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>First Name *</IonLabel>
                                    <IonInput value={formData.firstName} onIonInput={e => update('firstName', e.detail.value ?? '')}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                    {errors.firstName && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.firstName}</IonText>}
                                </div>
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Last Name *</IonLabel>
                                    <IonInput value={formData.lastName} onIonInput={e => update('lastName', e.detail.value ?? '')}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                    {errors.lastName && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.lastName}</IonText>}
                                </div>
                            </div>
                            {/* Row: Email */}
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Email Address *</IonLabel>
                                <IonInput type="email" value={formData.emailAddress} onIonInput={e => update('emailAddress', e.detail.value ?? '')}
                                    style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                {errors.emailAddress && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.emailAddress}</IonText>}
                            </div>
                            {/* Row: Organization */}
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Organization</IonLabel>
                                <IonInput value={formData.organization} onIonInput={e => update('organization', e.detail.value ?? '')}
                                    style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                            </div>
                            {/* Row: Passwords */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Temporary Password *</IonLabel>
                                    <IonInput type="password" value={formData.password} onIonInput={e => update('password', e.detail.value ?? '')}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                    {errors.password && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.password}</IonText>}
                                </div>
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Re-enter Password *</IonLabel>
                                    <IonInput type="password" value={formData.rePassword} onIonInput={e => update('rePassword', e.detail.value ?? '')}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                    {errors.rePassword && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.rePassword}</IonText>}
                                </div>
                            </div>
                            {/* Row: Checkboxes */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                <IonCheckbox checked={formData.isActive} onIonChange={e => update('isActive', e.detail.checked)}>Is Active</IonCheckbox>
                                <IonCheckbox checked={formData.isEnterpriseAdmin} onIonChange={e => {
                                    update('isEnterpriseAdmin', e.detail.checked);
                                    if (e.detail.checked) update('userTypeId', '');
                                }}>Is Enterprise Admin</IonCheckbox>
                                {!formData.isEnterpriseAdmin && (
                                    <IonCheckbox checked={formData.assignedToContract} onIonChange={e => update('assignedToContract', e.detail.checked)}>
                                        Is Assigned to Module
                                    </IonCheckbox>
                                )}
                            </div>
                            {/* User Type */}
                            {showUserType && (
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>User Type *</IonLabel>
                                    <IonSelect value={formData.userTypeId} onIonChange={e => update('userTypeId', e.detail.value)}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }}>
                                        {userTypes.map(ut => <IonSelectOption key={ut.Id} value={ut.Id.toString()}>{ut.Name}</IonSelectOption>)}
                                    </IonSelect>
                                    {errors.userTypeId && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.userTypeId}</IonText>}
                                </div>
                            )}
                            {/* Default Site */}
                            {showSiteSelect && (
                                <div>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Default Site</IonLabel>
                                    <IonSelect value={formData.defaultSiteId} onIonChange={e => update('defaultSiteId', e.detail.value)}
                                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }}>
                                        <IonSelectOption value="">None</IonSelectOption>
                                        {sites.filter(s => s.IsActive).map(s => <IonSelectOption key={s.Id} value={s.Id.toString()}>{s.Name}</IonSelectOption>)}
                                    </IonSelect>
                                </div>
                            )}
                        </div>
                    )}
                </IonContent>

                {!isSaving && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={onDidDismiss}>Cancel</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton fill="solid" color="primary" onClick={handleSave}>Save</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default AddUserModal;
